import { executeMutation } from 'libs/executeMutation';
import {
  createUsIorCbp5106,
  queryUsIorCbp,
  updateUsIorCbp5106,
  queryCbpByIorNumber,
} from 'libs/sharedQueries';
import { get } from 'lodash';
import { AccountType } from '@xbcb/shared-types';
import { UsIorCbpStatus } from '@xbcb/party-types';
import { setModal } from 'actions';
import { AppRecordKebabMenuItem } from 'routes';
import { ModalKey } from 'types';
import { checkKebabMenuItemForWorkOrderTask } from './checkKebabMenuItemForWorkOrderTask';
import { UsIorStatus } from '../../../__generated__/graphql';

export const createQueryUsIorKebabMenuItem = (
  pathToUsIor: string[] = [],
): AppRecordKebabMenuItem => ({
  key: 'queryUsIor',
  text: 'Query CBP',
  show: ({
    existingRecord,
    currentUser,
    workOrderTaskType,
    workOrderTaskEvaluatedState,
  }) =>
    currentUser.accountType === AccountType.OPERATOR &&
    get(existingRecord, [...pathToUsIor, 'iorNumber', 'value']) &&
    checkKebabMenuItemForWorkOrderTask(
      workOrderTaskType,
      workOrderTaskEvaluatedState,
    ),
  action: ({ record }) =>
    executeMutation({
      mutation: queryUsIorCbp,
      variables: { usIorId: get(record, [...pathToUsIor, 'id']) },
    }),
});

// Uses queryCbpByIorNumber for non-approved UsIors to avoid status updates that break CBP automation; tagged with usIorId so results still surface in the ABI data overview
// Uses queryUsIorCbp for approved UsIors where status preservation is no longer critical; adds to ABI data overview
export const createConditionalQueryUsIorKebabMenuItem = (
  pathToUsIor: string[] = [],
): AppRecordKebabMenuItem => ({
  key: 'conditionalQueryUsIor',
  text: 'Query CBP',
  show: ({
    existingRecord,
    currentUser,
    workOrderTaskType,
    workOrderTaskEvaluatedState,
  }) =>
    currentUser.accountType === AccountType.OPERATOR &&
    get(existingRecord, [...pathToUsIor, 'iorNumber', 'value']) &&
    checkKebabMenuItemForWorkOrderTask(
      workOrderTaskType,
      workOrderTaskEvaluatedState,
    ),
  action: ({ record }) => {
    const isActivated =
      get(record, [...pathToUsIor, 'status']) === UsIorStatus.APPROVED;
    const { mutation, variables } = isActivated
      ? {
          mutation: queryUsIorCbp,
          variables: { usIorId: get(record, [...pathToUsIor, 'id']) },
        }
      : {
          mutation: queryCbpByIorNumber,
          variables: {
            input: {
              iorNumber: get(record, [...pathToUsIor, 'iorNumber', 'value']),
              // Associate the standalone IOR query with this UsIor so its ABI
              // request/response messages are tagged usIorId and surface in the
              // ABI Data viewer (which searches by usIorId).
              tags: [
                {
                  key: 'usIorId',
                  value: get(record, [...pathToUsIor, 'id']),
                },
              ],
            },
          },
        };

    return executeMutation({ mutation, variables });
  },
});

export const createUsIorCreate5106KebabMenuItem = (
  pathToUsIor: string[] = [],
): AppRecordKebabMenuItem => ({
  key: 'createUsIorCbp5106',
  text: 'Add 5106',
  show: ({
    existingRecord,
    currentUser,
    workOrderTaskType,
    workOrderTaskEvaluatedState,
  }) =>
    currentUser.accountType === AccountType.OPERATOR &&
    [UsIorCbpStatus.NOT_ON_FILE, UsIorCbpStatus.INACTIVE].includes(
      get(existingRecord, [...pathToUsIor, 'cbpStatus']),
    ) &&
    get(existingRecord, [...pathToUsIor, 'iorNumber', 'value']) &&
    checkKebabMenuItemForWorkOrderTask(
      workOrderTaskType,
      workOrderTaskEvaluatedState,
    ),
  action: ({ record }) =>
    executeMutation({
      mutation: createUsIorCbp5106,
      variables: { usIorId: get(record, [...pathToUsIor, 'id']) },
    }),
});

export const createUsIorUpdate5106KebabMenuItem = (
  pathToUsIor: string[] = [],
): AppRecordKebabMenuItem => ({
  key: 'updateUsIorCbp5106',
  text: 'Update 5106 ',
  show: ({
    existingRecord,
    currentUser,
    workOrderTaskType,
    workOrderTaskEvaluatedState,
  }) =>
    currentUser.accountType === AccountType.OPERATOR &&
    [UsIorCbpStatus.ACTIVE_WITH_BOND, UsIorCbpStatus.ACTIVE_NO_BOND].includes(
      get(existingRecord, [...pathToUsIor, 'cbpStatus']),
    ) &&
    get(existingRecord, [...pathToUsIor, 'iorNumber', 'value']) &&
    checkKebabMenuItemForWorkOrderTask(
      workOrderTaskType,
      workOrderTaskEvaluatedState,
    ),
  action: ({ record }) =>
    executeMutation({
      mutation: updateUsIorCbp5106,
      variables: { usIorId: get(record, [...pathToUsIor, 'id']) },
    }),
});

export const createUsIorRequestCbpAssignedNumberKebabMenuItem = (
  pathToUsIor: string[] = [],
): AppRecordKebabMenuItem => ({
  key: 'requestUsIorCbpAssignedNumber',
  text: 'Request CBP Assigned Number',
  show: ({
    existingRecord,
    currentUser,
    workOrderTaskType,
    workOrderTaskEvaluatedState,
  }) => {
    return (
      currentUser.accountType === AccountType.OPERATOR &&
      !get(existingRecord, [...pathToUsIor, 'iorNumber', 'value']) &&
      checkKebabMenuItemForWorkOrderTask(
        workOrderTaskType,
        workOrderTaskEvaluatedState,
      )
    );
  },
  action: ({ dispatch, record }) => {
    dispatch(
      setModal({
        key: ModalKey.REQUEST_CBP_NUMBER,
        props: {
          visible: true,
          usIorId: get(record, [...pathToUsIor, 'id']),
        },
      }),
    );
  },
});
